import Http, { ApiHttpResponse } from "@/services/Http";
import Emitter from "@/services/Emitter";
import Cache from "@/services/Cache";
import Helper, { DateFormat } from "@/services/Helper";


import { TaskClockModel } from "@/models/TaskClockModel";
import { ContactModel, ClientModel } from "@/models/ClientModel";
import { RequestModel, JobModel, RequestFilter} from "@/models/RequestModel";
import { TimeModel, TimeModelFilter, TaskModel } from "@/models/TaskModel";
import { MileageModel, MileageModelFilter } from "@/models/MileageModel";
import { PartModel, PartModelFilter } from "@/models/PartModel";
import { PMModel, PMModelFilter } from "@/models/PMModel";
import { NoteModel, TaskNoteModel } from "@/models/TaskNoteModel";
import { ExpenseModel, ExpenseModelFilter } from "@/models/ExpenseModel";
import { TechnicianModel } from "@/models/TechnicianModel";
import { OrderStatusModel } from "@/models/OrderStatusModel";
import { PriorityModel } from "@/models/PriorityModel";
import { WorkOrderModel, WorkOrderFilter } from "@/models/WorkOrderModel";
import { WorkOrderItemModel, WorkOrderItemFilter } from "@/models/WorkOrderItemModel";
import { 
    TimeEntryModel, 
    TimeEntryFilter, 
    ClockStatusResponse, 
    ClockInRequest, 
    ClockOutRequest 
} from "@/models/TimeEntryModel";

// REPLACED ...
// import ns from "@/services/Api";

// Generic API Response interface
export interface ApiResponse<T = any> {
    Status: number;
    Message: string;
    Redirect: string;
    Meta: {
        Total?: number;
        Filter?: any;
    };
    Data: T;
}

// Helper function to create ApiResponse for methods that don't return full response
const createApiResponse = <T>(data: T, total?: number): ApiResponse<T> => ({
    Status: 1,
    Message: "",
    Redirect: "",
    Meta: {
        Total: total,
        Filter: {}
    },
    Data: data
});

const startSpin = (bg:boolean | undefined): void =>
{
    if (!bg)
    {
        Emitter.emit("loading", true);
    }
};

const stopSpin = (bg:boolean | undefined): void =>
{
    if (!bg)
    {
        Emitter.emit("loading", false);
    }
}

class ApiService
{


    //region Customers

    // TODO - change to loadAllClients and return array vs loadClients returns the raw response with filters etc.
    public async loadClients (bg?:boolean): Promise<ClientModel[]>
    {
        startSpin(bg);
        let rs = await Http.get("/company/list?page=0&size=999999&v=2");
        for (let i = 0; i < rs.length; i++)
        {
            rs[i] = new ClientModel(rs[i]);
        }
        stopSpin(bg);
        return rs;
    }

    public async loadClient (id:number | string, bg?:boolean): Promise<ClientModel> 
    {
        startSpin(bg);
        let rs = await Http.get("/company/details/" + id + "?v=2");
        rs = new ClientModel(rs);
        stopSpin(bg);
        return rs;
    };

    public async loadClientByCustomerNumber (cust:number | string, bg?:boolean): Promise<ClientModel>
    {
        startSpin(bg);
        let rs = await Http.get("/company/list?v=2", {
            CustomerNumber: cust
        });
        rs = new ClientModel(rs.length ? rs[0] : {});
        stopSpin(bg);
        return rs;
    }

    public async saveClient (client:ClientModel, bg?:boolean): Promise<ClientModel> 
    {
        let data = Helper.prep(client);
        startSpin(bg);
        try {
            let rs = await Http.post("/company/save?v=2", data);
            return new ClientModel(rs);
        } finally {
            stopSpin(bg);
        }
    }

    //endregion


    //region Clocks
    public async loadClocks (bg?: boolean): Promise<TaskModel[]>
    {
        startSpin(bg);
        let list = await Http.get("/TaskClock/List?v=2") || [];
        
        for (let i = 0; i < list.length; i++)
        {
            list[i] = new TaskModel(list[i]);
        }
        
        stopSpin(bg);
        return list;
    }

    public async loadClock (id: number | string, bg?: boolean): Promise<TaskModel>
    {
        startSpin(bg);
        let data = await Http.get("/TaskClock/Details/" + id + "?v=2");
        data = new TaskModel(data);
        stopSpin(bg);
        return data;
    }

    public async saveClock (clock: TaskClockModel, bg?: boolean): Promise<TaskClockModel>
    {
        let data = Helper.prep(clock);
        startSpin(bg);
        try {
            data = await Http.post("/TaskClock/Save?v=2", data) || {};
            return new TaskClockModel(data);
        } finally {
            stopSpin(bg);
        }
    };

    //#endregion


    //#region Requests

    public async loadRequests (filter: RequestFilter, bg?: boolean): Promise<ApiHttpResponse<RequestModel[]>>
    {
        startSpin(bg);
        
        // make sure certain items are set in the filter
        filter.IncludeContact = true;
        
        let response = await Http.getFullResponse<RequestModel[]>("/Request/List?v=2", filter);
        
        console.log('Raw API response:', response);
        
        // Process the data array and instantiate RequestModel objects
        if (response.Data && Array.isArray(response.Data)) {
            for (let i = 0; i < response.Data.length; i++) {
                response.Data[i] = new RequestModel(response.Data[i]);
            }
        }
        
        console.log('Final processed response:', response);
        
        stopSpin(bg);
        return response;
    }

    public async loadJobStatus (bg?: boolean) 
    {
        startSpin(bg);
        let rs = await Http.get("/JobStatus/List?v=2");
        stopSpin(bg);
        return rs;
    }

    public async loadOrderStatus (bg?: boolean): Promise<ApiResponse<OrderStatusModel[]>>
    {
        startSpin(bg);
        
        let response = await Http.getFullResponse<OrderStatusModel[]>("/OrderStatus/List?v=2");
        
        // Process the data array and instantiate OrderStatusModel objects
        if (response.Data && Array.isArray(response.Data)) {
            for (let i = 0; i < response.Data.length; i++) {
                response.Data[i] = new OrderStatusModel(response.Data[i]);
            }
        }
        
        stopSpin(bg);
        return response;
    }

    public async loadPriorities (bg?: boolean): Promise<ApiResponse<PriorityModel[]>>
    {
        startSpin(bg);
        
        let response = await Http.getFullResponse<PriorityModel[]>("/Priority/List?v=2");
        
        // Process the data array and instantiate PriorityModel objects
        if (response.Data && Array.isArray(response.Data)) {
            for (let i = 0; i < response.Data.length; i++) {
                response.Data[i] = new PriorityModel(response.Data[i]);
            }
        }
        
        stopSpin(bg);
        return response;
    }

    public async loadRequest (id: number | string, bg?: boolean): Promise<RequestModel>
    {
        startSpin(bg);
        let data = await Http.get("/Request/Details/" + id + "?v=2");
        data = new RequestModel(data);
        stopSpin(bg);
        return data;
    }

    public async saveRequest (request: RequestModel, bg?: boolean): Promise<RequestModel>
    {
        let data = Helper.prep(request);
        startSpin(bg);
        try {
            data = await Http.post("/Request/Save?v=2", data) || {};
            return new RequestModel(data);
        } finally {
            stopSpin(bg);
        }
    };

    public async loadClosedStatuses (bg?: boolean): Promise<string[]>
    {
        startSpin(bg);
        const response = await Http.get("/Request/ClosedStatuses?v=2");
        stopSpin(bg);
        return response || [];
    }

    //#endregion

    //#region Time

    public async loadTimes (filter: TimeModelFilter, bg?: boolean): Promise<ApiResponse<TimeModel[]>>
    {
        startSpin(bg);
        
        let response = await Http.getFullResponse<TimeModel[]>("/Time/List?v=2", filter);
        
        console.log('Raw API response:', response);
        
        // Process the data array and instantiate TimeModel objects
        if (response.Data && Array.isArray(response.Data)) {
            for (let i = 0; i < response.Data.length; i++) {
                response.Data[i] = new TimeModel(response.Data[i]);
            }
        }
        
        console.log('Final processed response:', response);
        
        stopSpin(bg);
        return response;
    }

    public async loadTime (id: number | string, bg?: boolean): Promise<TimeModel>
    {
        startSpin(bg);
        let data = await Http.get("/Time/Details/" + id + "?v=2");
        data = new TimeModel(data);
        stopSpin(bg);
        return data;
    }

    public async saveTime (time: TimeModel, bg?: boolean): Promise<TimeModel>
    {
        let data = Helper.prep(time);
        startSpin(bg);
        try {
            data = await Http.post("/Time/Save?v=2", data) || {};
            return new TimeModel(data as TaskModel);
        } finally {
            stopSpin(bg);
        }
    };

    //#endregin

    //#region Mileage

    public async loadMileages (filter: MileageModelFilter, bg?: boolean): Promise<ApiResponse<MileageModel[]>>
    {
        startSpin(bg);
        console.log('loadMileages called with filter:', filter);
        
        // Set required filter properties
        filter.IncludeRequest = true;
        filter.IncludeContact = true;
        
        let response = await Http.getFullResponse<MileageModel[]>("/Mileage/List?v=2", filter);
        console.log('loadMileages response:', response);
        
        // Process the Data array to instantiate MileageModel objects
        const list = (response.Data || []).map(item => new MileageModel(item));
        response.Data = list;
        
        stopSpin(bg);
        return response;
    }

    public async loadMileage (id: number | string, bg?: boolean): Promise<MileageModel>
    {
        startSpin(bg);
        let data = await Http.get("/Mileage/Details/" + id + "?v=2");
        data = new MileageModel(data);
        stopSpin(bg);
        return data;
    }

    public async saveMileage (mileage: MileageModel, bg?: boolean): Promise<MileageModel>
    {
        let data = Helper.prep(mileage);
        startSpin(bg);
        try {
            data = await Http.post("/Mileage/Save?v=2", data) || {};
            return new MileageModel(data);
        } finally {
            stopSpin(bg);
        }
    };

    //#endregion


    //#region Parts

    public async loadParts (filter: PartModelFilter, bg?: boolean): Promise<ApiResponse<PartModel[]>>
    {
        startSpin(bg);
        
        // make sure certain items are set in the filter
        filter.IncludeRequest = true;
        filter.IncludeContact = true;
        
        let response = await Http.getFullResponse<PartModel[]>("/Part/List?v=2", filter);
        
        console.log('Raw API response:', response);
        
        // Process the data array and instantiate PartModel objects
        if (response.Data && Array.isArray(response.Data)) {
            for (let i = 0; i < response.Data.length; i++) {
                response.Data[i] = new PartModel(response.Data[i]);
            }
        }
        
        console.log('Final processed response:', response);
        
        stopSpin(bg);
        return response;
    }

    public async loadPart (id: number | string, bg?: boolean): Promise<PartModel>
    {
        startSpin(bg);
        let data = await Http.get("/Part/Details/" + id + "?v=2");
        data = new PartModel(data);
        stopSpin(bg);
        return data;
    }

    public async savePart (part: PartModel, bg?: boolean): Promise<PartModel>
    {
        let data = Helper.prep(part);
        startSpin(bg);
        try {
            data = await Http.post("/Part/Save?v=2", data) || {};
            return new PartModel(data);
        } finally {
            stopSpin(bg);
        }
    };

    //#endregion


    //#region Expenses

    public async loadExpenses (filter:ExpenseModelFilter, bg:boolean | undefined): Promise<ApiResponse<ExpenseModel[]>>
    {
        startSpin(bg);
        console.log('loadExpenses called with filter:', filter);
        
        // Set required filter properties
        filter.IncludeRequest = true;
        filter.IncludeContact = true;
        
        let response = await Http.getFullResponse<ExpenseModel[]>("/Expense/List?v=2", filter);
        console.log('loadExpenses response:', response);
        
        // Process the Data array to instantiate ExpenseModel objects
        const list = (response.Data || []).map(item => new ExpenseModel(item));
        response.Data = list;
        
        stopSpin(bg);
        return response;
    };

    public async loadExpense (id:number | string, bg?:boolean | undefined): Promise<ExpenseModel>
    {
        startSpin(bg);
        let expense:ExpenseModel = await Http.get("/Expense/Details/" + id + "?v=2") || {} as ExpenseModel;
        stopSpin(bg);
        return new ExpenseModel(expense);
    };

    public async saveExpense (expense:ExpenseModel, bg?:boolean | undefined): Promise<ExpenseModel>
    {
        let data = Helper.prep(expense);
        console.debug("save expense", data);
        
        startSpin(bg);
        try {
            data = await Http.post("/Expense/Save?v=2", data) || {};
            return new ExpenseModel(data);
        } finally {
            stopSpin(bg);
        }
    };

    //#endregion


    //#region PMs

    public async loadPMs (filter: PMModelFilter, bg?: boolean): Promise<ApiResponse<PMModel[]>>
    {
        startSpin(bg);
        console.log('loadPMs called with filter:', filter);
        
        // Set required filter properties
        filter.IncludeContact = true;
        filter.IncludeTechnician = true;
        filter.IncludeLinkedRequest = true;
        
        let response = await Http.getFullResponse<PMModel[]>("/PM/List?v=2", filter);
        console.log('loadPMs response:', response);
        
        // Process the Data array to instantiate PMModel objects
        const list = (response.Data || []).map(item => new PMModel(item));
        response.Data = list;
        
        stopSpin(bg);
        return response;
    }

    public async loadPM (id: number | string, bg?: boolean): Promise<PMModel>
    {
        startSpin(bg);
        let pm = await Http.get("/PM/Details/" + id + "?v=2") || {} as PMModel;
        stopSpin(bg);
        return new PMModel(pm);
    }

    public async loadPMByRequest (requestId: number | string, bg?: boolean): Promise<PMModel | null>
    {
        startSpin(bg);
        let pm = await Http.get("/PM/ByRequest/" + requestId + "?v=2");
        stopSpin(bg);
        return pm ? new PMModel(pm) : null;
    }

    public async savePM (pm: PMModel, bg?: boolean): Promise<PMModel>
    {
        let data = Helper.prep(pm);
        console.debug("save PM", data);
        
        startSpin(bg);
        try {
            data = await Http.post("/PM/Save?v=2", data) || {};
            return new PMModel(data);
        } finally {
            stopSpin(bg);
        }
    }

    public async deletePM (id: number, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        try {
            let response = await Http.get("/PM/Delete/" + id + "?v=2");
            return response;
        } finally {
            stopSpin(bg);
        }
    }

    //#endregion

    //#region Notes

    public async loadNotes (filter: any, bg?: boolean): Promise<ApiResponse<TaskNoteModel[]>>
    {
        startSpin(bg);
        console.log('loadNotes called with filter:', filter);
        
        // Set required filter properties
        filter.IncludeRequest = true;
        filter.IncludeContact = true;
        
        let response = await Http.getFullResponse<TaskNoteModel[]>("/TaskNote/List?v=2", filter);
        console.log('loadNotes response:', response);
        
        // Process the Data array to instantiate TaskNoteModel objects
        const list = (response.Data || []).map(item => new NoteModel(item));
        response.Data = list;
        
        stopSpin(bg);
        return response;
    }

    public async loadNote (id: Number, bg?: boolean): Promise<TaskNoteModel>
    {
        startSpin(bg);
        let data = await Http.get("/TaskNote/Details/" + id + "?v=2");
        data = new NoteModel(data);
        stopSpin(bg);
        return data;
    }

    public async saveNote (note:TaskNoteModel, bg?: boolean): Promise<TaskNoteModel>
    {
        let data = Helper.prep(note);
        startSpin(bg);
        try {
            data = await Http.post("/TaskNote/Save?v=2", note);
            return new NoteModel(data);
        } finally {
            stopSpin(bg);
        }
    };

    /**
     * Load notes for a specific request/job (request-level notes, not task-level)
     */
    public async loadRequestNotes (requestId: number, bg?: boolean): Promise<TaskNoteModel[]>
    {
        startSpin(bg);
        try {
            const filter = {
                RequestId: [requestId],
                IncludeRequest: false,
                IncludeContact: false,
                Page: 0,
                Size: 1000,
                OrderBy: 'DateTaken',
                OrderDirection: 'desc'
            };
            let response = await Http.getFullResponse<TaskNoteModel[]>("/TaskNote/List?v=2", filter);
            // Filter to only include request-level notes (no TaskId)
            const notes = (response.Data || [])
                .filter((note: any) => !note.TaskId)
                .map((item: any) => {
                    const note = new NoteModel(item);
                    // Map AuthorName from the included Technician navigation property
                    (note as any).AuthorName = item.Technician?.TechnicianName || null;
                    // Parse IsSystemGenerated from Options JSON
                    try {
                        const opts = item.Options ? JSON.parse(item.Options) : {};
                        (note as any).IsSystemGenerated = opts.IsSystemGenerated === true;
                    } catch {
                        (note as any).IsSystemGenerated = false;
                    }
                    return note;
                });
            return notes;
        } finally {
            stopSpin(bg);
        }
    }

    /**
     * Save a request-level note (job note)
     */
    public async saveRequestNote (note: { RequestId: number; Note: string; IsSystemGenerated?: boolean }, bg?: boolean): Promise<TaskNoteModel>
    {
        startSpin(bg);
        try {
            const noteData = {
                RequestId: note.RequestId,
                Note: note.Note,
                Options: note.IsSystemGenerated ? JSON.stringify({ IsSystemGenerated: true }) : null,
                DateTaken: new Date().toISOString()
            };
            const data = await Http.post("/TaskNote/Save?v=2", noteData);
            return new NoteModel(data);
        } finally {
            stopSpin(bg);
        }
    }

    /**
     * Delete a request note
     */
    public async deleteRequestNote (noteId: string, bg?: boolean): Promise<void>
    {
        startSpin(bg);
        try {
            await Http.post("/TaskNote/Delete?v=2", { Id: noteId });
        } finally {
            stopSpin(bg);
        }
    }

    //#endregion

    //#region Techs

    public async loadTech (id:number, bg?:boolean): Promise<TechnicianModel>
    {
        startSpin(bg);
        let rs = await Http.get("/user/details/" + id + "?v=2");
        rs.Email = rs.Credential.Key;
        let tech = new TechnicianModel(rs);
        stopSpin(bg);
        return tech;
    }

    public async loadTechs (bg?:boolean) :Promise<Array<TechnicianModel>>
    {
        startSpin(bg);
        let rs = await Http.get("/user/list?page=0&size=999999&v=2");

        for (let i = 0; i < rs.length; i++)
        {
            rs[i] = new TechnicianModel(rs[i]);
            rs[i].Email = rs[i].Credential.Key;
        }

        stopSpin(bg);
        return rs;
    }

    public async saveTech (tech:TechnicianModel, bg?:boolean): Promise<TechnicianModel>
    {
        let data = Helper.prep(tech);
        startSpin(bg);
        try {
            data = await Http.post("/Technician/Save?v=2", data) || {};
            return new TechnicianModel(data);
        } finally {
            stopSpin(bg);
        }
    }

    // #endregion

    //#region Profile Images

    public async loadContactImage(contactId: number, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.get("/Data/ContactImage/" + contactId + "?v=2");
        stopSpin(bg);
        return response || null;
    }

    public async loadTechnicianImage(techId: number, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.get("/Data/TechnicianImage/" + techId + "?v=2");
        stopSpin(bg);
        return response || null;
    }

    public async uploadProfileImage(file: File, type: string, entityId: number, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        
        const formData = new FormData();
        formData.append('File', file);
        formData.append('Type', type);
        
        if (type === 'contact') {
            formData.append('ClientId', entityId.toString());
        } else if (type === 'technician') {
            formData.append('OwnerId', entityId.toString());
        }
        
        let response = await Http.upload("/Data/Upload?v=2", formData);
        stopSpin(bg);
        return response?.Data || null;
    }

    public async deleteProfileImage(imageId: string, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.post("/Data/Delete/" + imageId + "?v=2", {});
        stopSpin(bg);
        return response?.Data || null;
    }

    //#endregion

    //#region WorkOrders

    public async loadWorkOrders(filter?: WorkOrderFilter, bg?: boolean): Promise<ApiHttpResponse<WorkOrderModel[]>>
    {
        startSpin(bg);
        // const params = filter ? Helper.prep(filter) : {};

        const params = filter || {};

        let response = await Http.getFullResponse<WorkOrderModel[]>("/WorkOrder/List?v=2", params);
        
        if (response.Data && Array.isArray(response.Data))
        {
            response.Data = response.Data.map((item: any) => new WorkOrderModel(item));
        }
        
        stopSpin(bg);
        return response;
    }

    public async loadWorkOrder(id: number | string, bg?: boolean): Promise<WorkOrderModel>
    {
        startSpin(bg);
        let rs = await Http.get("/WorkOrder/Details/" + id + "?v=2", {
            IncludeDeleted: true
        });
        rs = new WorkOrderModel(rs);
        stopSpin(bg);
        return rs;
    }

    public async saveWorkOrder(data: WorkOrderModel, bg?: boolean): Promise<WorkOrderModel>
    {
        const preparedData = Helper.prep(data);
        
        // Convert AdditionalData to JSON string if it exists
        if (data.AdditionalData) {
            preparedData.AdditionalData = JSON.stringify(data.AdditionalData);
        }
        
        // Remove IsDeleted as it's not mapped in the backend
        delete preparedData.IsDeleted;
        
        console.log("saveWorkOrder - NotifyUpdates:", data.NotifyUpdates, "Title:", data.Title, "Prepared:", preparedData);
        startSpin(bg);
        try {
            let rs = await Http.post("/WorkOrder/Save?v=2", preparedData);
            return new WorkOrderModel(rs);
        } finally {
            stopSpin(bg);
        }
    }

    public async deleteWorkOrder(id: number, bg?: boolean): Promise<boolean>
    {
        startSpin(bg);
        try {
            let response = await Http.post("/WorkOrder/Delete/" + id + "?v=2", {});
            return response?.Status === 1;
        } finally {
            stopSpin(bg);
        }
    }

    public async restoreWorkOrder(id: number, bg?: boolean): Promise<boolean>
    {
        startSpin(bg);
        try {
            let response = await Http.post("/WorkOrder/Restore/" + id + "?v=2", {});
            return response?.Status === 1;
        } finally {
            stopSpin(bg);
        }
    }

    public async isWorkOrderDeleted(id: number, bg?: boolean): Promise<boolean>
    {
        startSpin(bg);
        let response = await Http.get("/WorkOrder/IsDeleted/" + id + "?v=2");
        stopSpin(bg);
        return response === true;
    }

    public async hardDeleteWorkOrder(id: number, bg?: boolean): Promise<boolean>
    {
        startSpin(bg);
        try {
            let response = await Http.post("/WorkOrder/HardDelete/" + id + "?v=2", {});
            return response?.Status === 1;
        } finally {
            stopSpin(bg);
        }
    }

    //#endregion

    //#region WorkOrderItems

    public async loadWorkOrderItems(filter?: WorkOrderItemFilter, bg?: boolean): Promise<ApiHttpResponse<WorkOrderItemModel[]>>
    {
        startSpin(bg);
        const params = filter;  // ? Helper.prep(filter) : {};
        let response = await Http.getFullResponse<WorkOrderItemModel[]>("/WorkOrderItem/List?v=2", params);
        
        if (response.Data && Array.isArray(response.Data))
        {
            response.Data = response.Data.map((item: any) => new WorkOrderItemModel(item));
        }
        
        stopSpin(bg);
        return response;
    }

    public async loadWorkOrderItem(id: number | string, bg?: boolean): Promise<WorkOrderItemModel>
    {
        startSpin(bg);
        let rs = await Http.get("/WorkOrderItem/Details/" + id + "?v=2");
        rs = new WorkOrderItemModel(rs);
        stopSpin(bg);
        return rs;
    }

    public async saveWorkOrderItem(data: WorkOrderItemModel, bg?: boolean): Promise<WorkOrderItemModel>
    {
        startSpin(bg);
        try {
            let rs = await Http.post("/WorkOrderItem/Save?v=2", Helper.prep(data));
            return new WorkOrderItemModel(rs);
        } finally {
            stopSpin(bg);
        }
    }

    public async deleteWorkOrderItem(id: number, bg?: boolean): Promise<boolean>
    {
        startSpin(bg);
        try {
            let response = await Http.post("/WorkOrderItem/Delete/" + id);
            return response?.Status === 1;
        } finally {
            stopSpin(bg);
        }
    }

    public async tagEntities(data: { WorkOrderId: number, TaskIds?: number[], PartIds?: number[], ExpenseIds?: number[], MileageIds?: number[] }, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        try {
            return await Http.post("/WorkOrder/TagEntities?v=2", data);
        } finally {
            stopSpin(bg);
        }
    }

    public async untagEntities(data: { WorkOrderId: number, TaskIds?: number[], PartIds?: number[], ExpenseIds?: number[], MileageIds?: number[] }, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        try {
            return await Http.post("/WorkOrder/UntagEntities?v=2", data);
        } finally {
            stopSpin(bg);
        }
    }

    //#endregion

    //#region WorkOrder Files

    public async loadWorkOrderFiles(workOrderId: number | string, bg?: boolean): Promise<any[]>
    {
        if (!workOrderId) {
            return [];
        }
        startSpin(bg);
        // Send filter with arrays for Type and RefId
        // Http.get already returns data.Data, so response IS the Data array
        let response = await Http.get("/Data/List?v=2", { 
            Type: ["invoice"], 
            RefId: [workOrderId] 
        });
        stopSpin(bg);
        return response || [];
    }

    public async uploadWorkOrderFile(file: File, workOrderId: number, extra?: string, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        const formData = new FormData();
        formData.append('File', file);
        formData.append('Type', 'invoice');
        formData.append('RefId', workOrderId.toString());
        if (extra) {
            formData.append('Extra', extra);
        }
        let response = await Http.upload("/Data/Upload?v=2", formData);
        stopSpin(bg);
        return response?.Data || null;
    }

    public async deleteWorkOrderFile(fileId: number | string, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.post("/Data/Delete/" + fileId + "?v=2", {});
        stopSpin(bg);
        return response?.Data || null;
    }

    //#endregion

    //#region Admin - Roles & Claims

    public async loadRoles(bg?: boolean): Promise<any[]>
    {
        startSpin(bg);
        let response = await Http.get("/admin/roleclaims/roles");
        stopSpin(bg);
        return response || [];
    }

    public async loadRoleClaims(roleId: string, bg?: boolean): Promise<any[]>
    {
        startSpin(bg);
        let response = await Http.get(`/admin/roleclaims/roles/${roleId}/claims`);
        stopSpin(bg);
        return response || [];
    }

    public async saveRole(role: any, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.post("/admin/roleclaims/roles/save", role);
        stopSpin(bg);
        return response;
    }

    public async deleteRole(roleId: string, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.post(`/admin/roleclaims/roles/${roleId}/delete`, {});
        stopSpin(bg);
        return response;
    }

    public async addClaimToRole(roleId: string, claim: any, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.post(`/admin/roleclaims/roles/${roleId}/claims/add`, claim);
        stopSpin(bg);
        return response;
    }

    public async removeClaimFromRole(claimId: string, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.post(`/admin/roleclaims/claims/${claimId}/delete`, {});
        stopSpin(bg);
        return response;
    }

    public async loadAvailableClaims(bg?: boolean): Promise<any[]>
    {
        startSpin(bg);
        let response = await Http.get("/admin/roleclaims/available-claims");
        stopSpin(bg);
        return response || [];
    }

    public async loadTechniciansWithRoles(bg?: boolean): Promise<any[]>
    {
        startSpin(bg);
        let response = await Http.get("/admin/roleclaims/technicians");
        stopSpin(bg);
        return response || [];
    }

    public async addClaimToTechnician(techId: string, claim: any, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.post(`/admin/roleclaims/technicians/${techId}/claims/add`, claim);
        stopSpin(bg);
        return response;
    }

    public async removeClaimFromTechnician(techId: string, claimId: string, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.post(`/admin/roleclaims/technicians/${techId}/claims/${claimId}/delete`, {});
        stopSpin(bg);
        return response;
    }

    public async updateTechnicianRoles(techId: string, roleIds: string[], bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.post(`/admin/roleclaims/technicians/${techId}/roles/update`, roleIds);
        stopSpin(bg);
        return response;
    }

    //#endregion

    //#region Request Role Access Control

    /**
     * Get the roles assigned to a specific request (admin-only)
     */
    public async getRequestRoles(requestId: number, bg?: boolean): Promise<any[]>
    {
        startSpin(bg);
        let response = await Http.get(`/request/${requestId}/roles`);
        stopSpin(bg);
        return response || [];
    }

    /**
     * Update the roles assigned to a request (admin-only)
     * Replaces all existing role assignments with the provided list
     */
    public async updateRequestRoles(requestId: number, roleIds: string[], bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.post(`/request/${requestId}/roles/update`, roleIds);
        stopSpin(bg);
        return response;
    }

    /**
     * Add a single role to a request (admin-only)
     */
    public async addRequestRole(requestId: number, roleId: string, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.post(`/request/${requestId}/roles/add/${roleId}`, {});
        stopSpin(bg);
        return response;
    }

    /**
     * Remove a single role from a request (admin-only)
     */
    public async removeRequestRole(requestId: number, roleId: string, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.post(`/request/${requestId}/roles/remove/${roleId}`, {});
        stopSpin(bg);
        return response;
    }

    //#endregion

    //#region Admin - Tenants

    public async loadTenants(bg?: boolean): Promise<any[]>
    {
        startSpin(bg);
        let response = await Http.get("/admin/tenants");
        stopSpin(bg);
        return response || [];
    }

    public async loadSubscriptionTiers(bg?: boolean): Promise<any[]>
    {
        startSpin(bg);
        let response = await Http.get("/admin/tenants/subscription-tiers");
        stopSpin(bg);
        return response || [];
    }

    public async loadTenantUsage(tenantId: number, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.get(`/admin/tenants/${tenantId}/usage`);
        stopSpin(bg);
        return response;
    }

    public async saveTenant(tenant: any, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.post("/admin/tenants/save", tenant);
        stopSpin(bg);
        return response;
    }

    public async startImpersonation(tenantId: number, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.post("/admin/impersonation/start", { tenantId });
        stopSpin(bg);
        return response;
    }

    //#endregion

    //#region Admin - System Logs

    public async loadSystemLogs(params: any, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.getFullResponse("/log", params);
        stopSpin(bg);
        return response;
    }

    public async loadLogStats(bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.get("/log/stats");
        stopSpin(bg);
        return response;
    }

    //#endregion

    //#region Admin - Migrations

    public async loadTenantMigrations(bg?: boolean): Promise<any[]>
    {
        startSpin(bg);
        let response = await Http.get("/admin/migrations/tenants");
        stopSpin(bg);
        return response || [];
    }

    public async loadTenantMigrationStatus(schema: string, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.get(`/admin/migrations/tenant/${schema}`);
        stopSpin(bg);
        return response;
    }

    //#endregion

    //#region Email

    public async sendEmail(data: { to: string; subject: string; body: string }, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.post("/email/send", data);
        stopSpin(bg);
        return response;
    }

    public async sendEmailWithAttachment(formData: FormData, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.upload("/email/send-with-attachment", formData);
        stopSpin(bg);
        return response;
    }

    public async getEmailLogs(filter?: { status?: string; pageSize?: number; page?: number }, bg?: boolean): Promise<EmailLogModel[]>
    {
        startSpin(bg);
        const params: any = {};
        if (filter?.status) params.status = filter.status;
        if (filter?.pageSize) params.pageSize = filter.pageSize;
        if (filter?.page) params.page = filter.page;
        
        let response = await Http.get("/email/logs", params);
        stopSpin(bg);
        return response ?? [];
    }

    public async sendEmailNow(emailLogId: string, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.post(`/email/send-now/${emailLogId}`, {});
        stopSpin(bg);
        return response;
    }

    public async resendEmail(emailLogId: string, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.post(`/email/resend/${emailLogId}`, {});
        stopSpin(bg);
        return response;
    }

    //#endregion

    //#region Auth

    /**
     * Refresh the JWT token by fetching fresh user data from the database.
     * This should be called after profile/role updates to ensure the token
     * reflects the latest user permissions.
     */
    public async refreshToken(bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.post("/auth/keepalive", {});
        stopSpin(bg);
        return response;
    }

    /**
     * Register a new user - stores registration for admin review
     */
    public async register(data: { name: string; email: string; phone?: string; domain?: string; password: string }, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.post("/auth/register", data);
        stopSpin(bg);
        return response;
    }

    /**
     * Request a password reset email
     */
    public async forgotPassword(data: { email: string; domain?: string }, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.post("/auth/forgot-password", data);
        stopSpin(bg);
        return response;
    }

    /**
     * Validate a password reset token
     */
    public async validateResetToken(token: string, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.post("/auth/validate-reset-token", { Token: token });
        stopSpin(bg);
        return response;
    }

    /**
     * Reset password using validated token
     */
    public async resetPassword(data: { resetToken: string; newPassword: string }, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.post("/auth/reset-password", { ResetToken: data.resetToken, NewPassword: data.newPassword });
        stopSpin(bg);
        return response;
    }

    /**
     * Get all pending registrations (admin only)
     */
    public async getRegistrations(bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.get("/admin/registrations");
        stopSpin(bg);
        return response;
    }

    //#endregion

    //#region Staff

    public async loadStaff(filter?: any, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.getFullResponse("/staff/list", filter);
        stopSpin(bg);
        return response;
    }

    public async loadStaffDetails(id: string, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.get(`/staff/details/${id}`);
        stopSpin(bg);
        return response?.Data;
    }

    //#endregion

    //#region RequestStaffMap

    public async getRequestStaff(requestId: number, bg?: boolean): Promise<any[]>
    {
        startSpin(bg);
        let response = await Http.get(`/RequestStaffMap/ByRequest/${requestId}`);
        stopSpin(bg);
        return response?.Data ?? [];
    }

    public async assignStaffToRequest(requestId: number, staffId: string, notifyStatus: number = 0, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.post("/RequestStaffMap/AssignStaff", {
            RequestId: requestId,
            StaffId: staffId,
            NotifyStatus: notifyStatus
        });
        stopSpin(bg);
        return response?.Data;
    }

    public async removeStaffFromRequest(mappingId: string, bg?: boolean): Promise<boolean>
    {
        startSpin(bg);
        let response = await Http.post(`/RequestStaffMap/Delete/${mappingId}`, {});
        stopSpin(bg);
        return response?.Status === 1;
    }

    //#endregion

    //#region Notifications

    public async getNotifications(filter?: any, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.getFullResponse("/Notification/list", filter);
        stopSpin(bg);
        return response;
    }

    public async getNotification(id: number, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.getFullResponse(`/Notification/${id}`);
        stopSpin(bg);
        return response;
    }

    public async getUnreadNotificationCount(bg?: boolean): Promise<number>
    {
        startSpin(bg);
        let response = await Http.getFullResponse("/Notification/unread-count");
        stopSpin(bg);
        return response?.Data?.UnreadCount || 0;
    }

    public async saveNotification(notification: any, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.post("/Notification/save", notification);
        stopSpin(bg);
        return response;
    }

    public async markNotificationRead(id: number, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.post(`/Notification/mark-read/${id}`, {});
        stopSpin(bg);
        return response;
    }

    public async markAllNotificationsRead(bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.post("/Notification/mark-all-read", {});
        stopSpin(bg);
        return response;
    }

    public async deleteNotification(id: number, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.post(`/Notification/delete/${id}`, {});
        stopSpin(bg);
        return response;
    }

    public async deleteMultipleNotifications(ids: number[], bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.post("/Notification/delete-multiple", ids);
        stopSpin(bg);
        return response;
    }

    //#endregion

    //#region Form Templates

    public async loadFormTemplates(filter?: FormTemplateFilter, bg?: boolean): Promise<ApiResponse<FormTemplateModel[]>>
    {
        startSpin(bg);
        let response = await Http.getFullResponse<FormTemplateModel[]>("/FormTemplate/List", filter || {});
        stopSpin(bg);
        return response;
    }

    public async getFormTemplate(id: string, bg?: boolean): Promise<ApiResponse<FormTemplateModel>>
    {
        startSpin(bg);
        let response = await Http.getFullResponse<FormTemplateModel>(`/FormTemplate/Details/${id}`);
        stopSpin(bg);
        return response;
    }

    public async saveFormTemplate(model: FormTemplateModel, bg?: boolean): Promise<FormTemplateModel>
    {
        startSpin(bg);
        let response = await Http.post("/FormTemplate/Save", model);
        stopSpin(bg);
        return response;
    }

    public async deleteFormTemplate(id: string, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.post(`/FormTemplate/Delete/${id}`, {});
        stopSpin(bg);
        return response;
    }

    //#endregion

    //#region Form Submissions

    public async loadFormSubmissions(filter?: FormSubmissionFilter, bg?: boolean): Promise<ApiResponse<FormSubmissionModel[]>>
    {
        startSpin(bg);
        let response = await Http.getFullResponse<FormSubmissionModel[]>("/FormSubmission/List", filter || {});
        stopSpin(bg);
        return response;
    }

    public async getFormSubmission(id: string, bg?: boolean): Promise<ApiResponse<FormSubmissionModel>>
    {
        startSpin(bg);
        let response = await Http.getFullResponse<FormSubmissionModel>(`/FormSubmission/Details/${id}`);
        stopSpin(bg);
        return response;
    }

    public async saveFormSubmission(model: FormSubmissionModel, bg?: boolean): Promise<FormSubmissionModel>
    {
        startSpin(bg);
        let response = await Http.post("/FormSubmission/Save", model);
        stopSpin(bg);
        return response;
    }

    public async submitForm(id: string, bg?: boolean): Promise<FormSubmissionModel>
    {
        startSpin(bg);
        let response = await Http.post(`/FormSubmission/Submit/${id}`, {});
        stopSpin(bg);
        return response;
    }

    public async deleteFormSubmission(id: string, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.post(`/FormSubmission/Delete/${id}`, {});
        stopSpin(bg);
        return response;
    }

    //#endregion

    //#region Form Access Links

    public async loadFormAccessLinks(filter?: FormAccessLinkFilter, bg?: boolean): Promise<ApiResponse<FormAccessLinkModel[]>>
    {
        startSpin(bg);
        let response = await Http.getFullResponse<FormAccessLinkModel[]>("/FormAccessLink/List", filter || {});
        stopSpin(bg);
        return response;
    }

    public async getFormAccessLinksBySubmission(submissionId: string, bg?: boolean): Promise<ApiResponse<FormAccessLinkModel[]>>
    {
        startSpin(bg);
        let response = await Http.getFullResponse<FormAccessLinkModel[]>(`/FormAccessLink/BySubmission/${submissionId}`);
        stopSpin(bg);
        return response;
    }

    public async createFormAccessLink(request: CreateAccessLinkRequest, bg?: boolean): Promise<FormAccessLinkModel>
    {
        startSpin(bg);
        let response = await Http.post("/FormAccessLink/Create", request);
        stopSpin(bg);
        return response;
    }

    public async revokeFormAccessLink(id: string, revokedBy?: number, bg?: boolean): Promise<FormAccessLinkModel>
    {
        startSpin(bg);
        let response = await Http.post(`/FormAccessLink/Revoke/${id}`, { RevokedBy: revokedBy });
        stopSpin(bg);
        return response;
    }

    public async deleteFormAccessLink(id: string, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.post(`/FormAccessLink/Delete/${id}`, {});
        stopSpin(bg);
        return response;
    }

    public async getFormAccessLinksByTemplate(templateId: string, bg?: boolean): Promise<ApiResponse<FormAccessLinkModel[]>>
    {
        startSpin(bg);
        let response = await Http.getFullResponse<FormAccessLinkModel[]>(`/FormAccessLink/ByTemplate/${templateId}`);
        stopSpin(bg);
        return response;
    }

    public async createFormAccessLinkForTemplate(request: CreateTemplateAccessLinkRequest, bg?: boolean): Promise<ApiResponse<CreateTemplateAccessLinkResponse>>
    {
        startSpin(bg);
        let response = await Http.postFullResponse<CreateTemplateAccessLinkResponse>("/FormAccessLink/CreateForTemplate", request);
        stopSpin(bg);
        return response;
    }

    //#endregion

    //#region Time Clock (User Clock In/Out)

    /**
     * Get current clock status for user
     */
    public async getClockStatus(userId?: number, bg?: boolean): Promise<ClockStatusResponse>
    {
        startSpin(bg);
        const params = userId ? { userId } : {};
        let response = await Http.get("/TimeEntry/Status", params);
        stopSpin(bg);
        return response;
    }

    /**
     * Clock in the user
     */
    public async clockIn(request?: ClockInRequest, bg?: boolean): Promise<TimeEntryModel>
    {
        startSpin(bg);
        let response = await Http.post("/TimeEntry/ClockIn", request || {});
        stopSpin(bg);
        return response;
    }

    /**
     * Clock out the user
     */
    public async clockOut(request?: ClockOutRequest, bg?: boolean): Promise<TimeEntryModel>
    {
        startSpin(bg);
        let response = await Http.post("/TimeEntry/ClockOut", request || {});
        stopSpin(bg);
        return response;
    }

    /**
     * Load time entries with filter
     */
    public async loadTimeEntries(filter?: TimeEntryFilter, bg?: boolean): Promise<ApiResponse<TimeEntryModel[]>>
    {
        startSpin(bg);
        let response = await Http.getFullResponse<TimeEntryModel[]>("/TimeEntry/List", filter || {});
        stopSpin(bg);
        return response;
    }

    /**
     * Get a single time entry by ID
     */
    public async getTimeEntry(id: string, bg?: boolean): Promise<TimeEntryModel>
    {
        startSpin(bg);
        let response = await Http.get(`/TimeEntry/Details/${id}`);
        stopSpin(bg);
        return response;
    }

    /**
     * Save a time entry
     */
    public async saveTimeEntry(entry: TimeEntryModel, bg?: boolean): Promise<TimeEntryModel>
    {
        startSpin(bg);
        let response = await Http.post("/TimeEntry/Save", entry);
        stopSpin(bg);
        return response;
    }

    //#endregion

    //region Settings & Payments

    public async getOptions(category: string, bg?: boolean): Promise<any[]> {
        startSpin(bg);
        const rs = await Http.get(`/options?prefix=${category}`);
        stopSpin(bg);
        return rs;
    }

    public async saveOptions(category: string, options: Array<{ Key: string; Value: string }>, bg?: boolean): Promise<void> {
        startSpin(bg);
        const payload = {
            Options: options.map(opt => ({
                OptionKey: `${category}${opt.Key}`,
                OptionValue: opt.Value
            }))
        };
        await Http.post('/options/bulk', payload);
        stopSpin(bg);
    }

    public async getTenantUsers(bg?: boolean): Promise<any[]> {
        startSpin(bg);
        const rs = await Http.get('/settings/users');
        stopSpin(bg);
        return rs;
    }

    public async updateUserRole(userId: number, role: string, bg?: boolean): Promise<void> {
        startSpin(bg);
        await Http.post(`/settings/users/${userId}/role`, { Role: role });
        stopSpin(bg);
    }

    public async testEmail(bg?: boolean): Promise<void> {
        startSpin(bg);
        await Http.post('/settings/email/test', {});
        stopSpin(bg);
    }

    public async getPaymentCheckouts(bg?: boolean): Promise<any[]> {
        startSpin(bg);
        const rs = await Http.get('/payments/checkouts');
        stopSpin(bg);
        return rs;
    }

    public async getInvoiceByHash(invoiceId: number | string, hash: string, bg?: boolean): Promise<any> {
        startSpin(bg);
        const rs = await Http.get(`/invoice/${invoiceId}/view/${hash}`);
        stopSpin(bg);
        return rs;
    }

    public async createSquareCheckout(workOrderId: number, bg?: boolean): Promise<any> {
        startSpin(bg);
        const rs = await Http.post('/payments/square/checkout', { WorkOrderId: workOrderId });
        stopSpin(bg);
        return rs;
    }

    public async testSquareConnection(bg?: boolean): Promise<void> {
        startSpin(bg);
        await Http.post('/settings/square/test', {});
        stopSpin(bg);
    }

    //#endregion
}

// Form Template interfaces
export interface FormTemplateModel {
    Id?: string;
    Title: string;
    Description?: string;
    RefType: string;
    RefId?: string;
    FormSchema: string;
    CreatedBy?: number;
    ModifiedBy?: number;
    DateCreated?: string;
    DateModified?: string;
    IsActive: boolean;
    Category?: string;
    Version: number;
}

export interface FormTemplateFilter {
    Id?: string[];
    Title?: string;
    RefType?: string[];
    RefId?: string;
    CreatedBy?: number[];
    IsActive?: boolean;
    Category?: string;
    CreatedAfter?: string;
    CreatedBefore?: string;
    SearchTerm?: string;
    Page?: number;
    Size?: number;
}

// Form Submission interfaces
export interface FormSubmissionModel {
    Id?: string;
    TemplateId: string;
    Template?: FormTemplateModel;
    SubmittedBy?: number;
    SubmitterName?: string;
    SubmitterEmail?: string;
    SubmitterIP?: string;
    FormData: string;
    CompanyId?: number;
    RequestId?: number;
    DateCreated?: string;
    DateModified?: string;
    DateSubmitted?: string;
    Status: number;
    Notes?: string;
}

export interface FormSubmissionFilter {
    Id?: string[];
    TemplateId?: string[];
    SubmittedBy?: number[];
    SubmitterName?: string;
    SubmitterEmail?: string;
    CompanyId?: number[];
    RequestId?: number[];
    Status?: number[];
    CreatedAfter?: string;
    CreatedBefore?: string;
    SubmittedAfter?: string;
    SubmittedBefore?: string;
    SearchTerm?: string;
    IncludeTemplate?: boolean;
    Page?: number;
    Size?: number;
}

// Form Access Link interfaces
export interface FormAccessLinkModel {
    Id?: string;
    TemplateId?: string;
    Template?: FormTemplateModel;
    SubmissionId?: string;
    Submission?: FormSubmissionModel;
    SecretKey: string;
    AllowEditing: boolean;
    DateCreated?: string;
    DateExpires?: string;
    DateRevoked?: string;
    CreatedBy?: number;
    RevokedBy?: number;
    IsActive: boolean;
    SentToEmail?: string;
    AccessCount: number;
    LastAccessed?: string;
    MaxUses?: number;
}

export interface FormAccessLinkFilter {
    Id?: string[];
    TemplateId?: string[];
    SubmissionId?: string[];
    SecretKey?: string;
    CreatedBy?: number[];
    IsActive?: boolean;
    IsRevoked?: boolean;
    IsExpired?: boolean;
    AllowEditing?: boolean;
    CreatedAfter?: string;
    CreatedBefore?: string;
    IncludeSubmission?: boolean;
    IncludeTemplate?: boolean;
    Page?: number;
    Size?: number;
}

export interface CreateAccessLinkRequest {
    SubmissionId: string;
    CreatedBy?: number;
    ExpiresAt?: string;
    MaxUses?: number;
    SentToEmail?: string;
    AllowEditing?: boolean;
}

export interface CreateTemplateAccessLinkRequest {
    TemplateId: string;
    AllowEditing?: boolean;
    ExpiresAt?: string;
    MaxUses?: number;
    SentToEmail?: string;
}

export interface CreateTemplateAccessLinkResponse {
    Link: FormAccessLinkModel;
    PublicUrl: string;
}

export interface EmailLogModel {
    EmailLogId: string;
    ToAddresses: string;
    CcAddresses?: string;
    FromAddress?: string;
    Subject: string;
    BodyJson?: string;
    Status: string;
    ErrorMessage?: string;
    RetryCount: number;
    CreatedDate: string;
    SentDate?: string;
    LastRetryDate?: string;
}

const singleton = new ApiService();
export default singleton;